// @ts-check

import React from "react";
import { Alert, Button, Select } from "antd";
import {
  PlusOutlined,
} from "@ant-design/icons";

import { RowInfos } from "../row-infos";

/**
 * @typedef {Object} Props
 *
 * @prop {import("../domain").ImportState['reducedRows']} reducedRows
 * @prop {import("../domain").ImportField} relationshipField
 * @prop {import("../domain").RelationshipMatchConfig} relationshipMatchConfig
 * @prop {import("../import-state/import-state").ImportStateService} service
 * @prop {{label: string; value: string;}[]} dataIndexOptions
 */

// TODO: Probably need to support not updating anything? 
// For example, what if there are no records in the system 
// to update? Then it is just an update.

/**
 * @param {Props} props
 */
export const ConfigureRelationshipMatch = (props) => {
  const {
    dataIndexOptions,
    reducedRows,
    relationshipField,
    relationshipMatchConfig,
    service,
  } = props;

  if (relationshipField.type !== "SET") {
    throw new Error("relationshipField should be SET");
  }

  /**
   * @type {JSX.Element[]}
   */
  const successes = [];

  /**
   * @param {import("../domain").ImportRowInfo} info
   * 
   * @returns {info is import("../domain").RelationshipMatchInfo}
   */
  const isRelationshipMatchInfo = (info) => {
    return info.type === "relationship_match_info" && info.dataIndex === relationshipField.dataIndex;
  }

  let total = 0;
  for (const reducedRow of reducedRows) {
    const info = reducedRow.info.find(isRelationshipMatchInfo);

    if (!info) {
      continue;
    }

    for (const value of info.splitValues) {
      if (value.isValid) {
        total = total + 1;
      }
    }
  }

  if (total) {
    successes.push(
      <React.Fragment key="update_success">
        connect <strong>{total}</strong> matched relationships
      </React.Fragment>
    );
  }

  /**
   * @type {JSX.Element[]}
   */
  const issues = [];

  const hasSelections = !!relationshipMatchConfig.dataIndexes.flat().length;
  if (!hasSelections) {
    issues.push(
      <div className="mb-2">
        Must make a selection to match the relationship field.
      </div>
    );
  }

  if (hasSelections && !successes.length) {
    issues.push(
      <div>No matches were found. This import will not relate any {relationshipField.name} records.</div>
    );
  }

  return (
    <div className="w-full">
      {relationshipMatchConfig.dataIndexes.map((dataIndexes, rowIndex) => {
        const unusedOptions = dataIndexOptions.filter((option) => {
          return !dataIndexes.includes(option.value);
        });

        return (
          <React.Fragment key={rowIndex}>
            <div className="mb-2 flex items-center">
              {dataIndexes.map((dataIndex, columnIndex) => {
                return (
                  <React.Fragment key={dataIndex}>
                    <Select
                      allowClear={dataIndexes.length > 1 || service.canRemoveEntityMatchKey()}
                      className="flex-1"
                      onClear={() => {
                        const next = [...dataIndexes];
                        next.splice(columnIndex, 1);

                        if (!next.length) {
                          service.removeRelationshipMatchKey({
                            dataIndex: relationshipField.dataIndex,
                            index: rowIndex,
                          });
                          return
                        }

                        service.setRelationshipMatchKey({
                          dataIndex: relationshipField.dataIndex,
                          index: rowIndex,
                          value: next,
                        });
                      }}
                      onSelect={(value) => {
                        const next = [...dataIndexes];
                        next.splice(columnIndex, 1, value);

                        service.setRelationshipMatchKey({
                          value: next,
                          dataIndex: relationshipField.dataIndex,
                          index: rowIndex
                        });
                      }}
                      options={dataIndexOptions}
                      value={dataIndex}
                    />
                    {columnIndex + 1 < dataIndexes.length ? (
                      <div className="mx-2 font-bold">And</div>
                    ) : null}
                  </React.Fragment>
                );
              })}
              {unusedOptions.length && dataIndexes.length < 3 ? (
                <Button
                  rootClassName="ml-2"
                  size="small"
                  icon={<PlusOutlined />}
                  iconPosition="end"
                  onClick={() => {
                    service.setRelationshipMatchKey({
                      value: [...dataIndexes, unusedOptions[0].value],
                      dataIndex: relationshipField.dataIndex,
                      index: rowIndex,
                    })
                  }}
                >
                  And
                </Button>
              ) : null}
            </div>
            {rowIndex + 1 < relationshipMatchConfig.dataIndexes.length ? (
              <div className="my-1 font-bold">Or</div>
            ) : null}
          </React.Fragment>
        )
      })}
      {
        service.canAddRelationshipMatchKey()(relationshipField.dataIndex) && (
          <div className="flex justify-start mb-2">
            <Button
              onClick={() => service.addRelationshipMatchKey(relationshipField.dataIndex)}
              icon={<PlusOutlined />}
              iconPosition="end"
              size="small"
            >Or</Button>
          </div>
        )
      }

      <RowInfos
        reducedRows={reducedRows}
        filter={(info) => {
          if (info.level === "info") {
            return false;
          }

          if (info.type !== "relationship_match_info") {
            return false;
          }

          return relationshipField.dataIndex === info.dataIndex;
        }}
      />

      {
        issues.length
          ? issues.map((issue, index) => {
            return (
              <Alert
                message={issue}
                type="warning"
                className="!my-2"
                key={index}
              />
            );
          })
          : null
      }

      {
        successes.length ? (
          <Alert
            type="success"
            message={<div>Import will {successes} records.</div>}
            className="!mb-2"
          />
        ) : null
      }
    </div >
  );
};
